// assets/js/controllers/datatable_controller.ts

import { Controller } from "@hotwired/stimulus"

import DataTable, { Api } from "datatables.net-bs5"

import Swal from "sweetalert2"

import $ from "jquery"

import "datatables.net-responsive"
import "datatables.net-responsive-bs5"

import "datatables.net-bs5/css/dataTables.bootstrap5.css"
import "datatables.net-responsive-bs5/css/responsive.bootstrap5.css"

/* ==============================
 * CONTROLLER
 * ============================== */

export default class extends Controller {

    static targets = ["table"]

    static values = {
        url: String,
        columns: Array,
        order: Array,
        pageLength: { type: Number, default: 50 },
        languageUrl: { type: String, default: 'https://cdn.datatables.net/plug-ins/1.13.7/i18n/es-ES.json' },
    }

    declare readonly tableTarget: HTMLTableElement

    declare urlValue: string

    declare columnsValue: any[]

    declare orderValue: any[]

    declare pageLengthValue: number

    declare languageUrlValue: string

    datatable: Api | null = null

    /*
     * Legacy selection state — kept for listings that still use the
     * old `.dt-checkbox` / `[data-dt-select-all]` pattern. New listings
     * should use the `bulk-actions` controller instead.
     */
    legacySelectedIds = new Set<string>()

    /* ==============================
     * LIFECYCLE
     * ============================== */

    connect(): void {

        if (DataTable.isDataTable(this.tableTarget)) {

            this.datatable = new DataTable(
                this.tableTarget as HTMLTableElement
            )

        } else {

            this.initDataTable()
        }

        this.initializeFilters()

        this.element.addEventListener(
            "click",
            this.onClick
        )

        document.addEventListener(
            "turbo:before-cache",
            this.beforeCache
        )

        this.element.addEventListener(
            "saved-filters:applied",
            this.onSavedFiltersApplied
        )

        this.element.addEventListener(
            "bulk-actions:reload",
            this.onReloadRequest
        )
    }

    disconnect(): void {

        this.element.removeEventListener(
            "click",
            this.onClick
        )

        document.removeEventListener(
            "turbo:before-cache",
            this.beforeCache
        )

        this.element.removeEventListener(
            "saved-filters:applied",
            this.onSavedFiltersApplied
        )

        this.element.removeEventListener(
            "bulk-actions:reload",
            this.onReloadRequest
        )

        this.legacySelectedIds.clear()
    }

    /* ==============================
     * PUBLIC API
     * ============================== */

    /**
     * Reload the datatable (used by sibling controllers via outlets / events).
     */
    reload(holdPagination = true): void {

        this.datatable?.ajax.reload(
            undefined,
            !holdPagination
        )
    }

    /* ==============================
     * DATATABLE
     * ============================== */

    initDataTable(): void {

        this.datatable = new DataTable(
            this.tableTarget as HTMLTableElement,
            {

                ajax: {

                    url: this.urlValue,

                    dataSrc: "data",

                    data: (d: any) => {

                        // =========================
                        // GENERIC FILTERS
                        // =========================

                        const filters =
                            document.querySelectorAll<HTMLElement>(
                                "[data-dt-filter]"
                            )

                        filters.forEach((filter) => {

                            const key =
                                filter.dataset.dtFilter

                            if (!key) {
                                return
                            }

                            // =====================
                            // RADIO BUTTON
                            // =====================

                            if (
                                filter instanceof HTMLInputElement
                                &&
                                filter.type === "radio"
                            ) {

                                if (filter.checked) {
                                    d[key] = filter.value
                                }

                                return
                            }

                            // =====================
                            // CHECKBOX
                            // =====================

                            if (
                                filter instanceof HTMLInputElement
                                &&
                                filter.type === "checkbox"
                            ) {

                                d[key] = filter.checked

                                return
                            }

                            // =====================
                            // SELECT MULTIPLE
                            // =====================

                            if (
                                filter instanceof HTMLSelectElement
                                &&
                                filter.multiple
                            ) {

                                /*
                                 * TomSelect
                                 */
                                const tomSelect =
                                    (filter as any).tomselect;

                                if (tomSelect) {

                                    d[key] =
                                        tomSelect.getValue();

                                    return;
                                }

                                /*
                                 * Native select
                                 */
                                d[key] = Array.from(
                                    filter.selectedOptions
                                ).map(
                                    option => option.value
                                )

                                return
                            }

                            // =====================
                            // INPUT / SELECT
                            // =====================

                            d[key] = (
                                filter as HTMLInputElement
                            ).value
                        })

                        return d
                    }
                },

                serverSide: true,

                processing: true,

                paging: true,

                searching: true,

                responsive: true,

                pageLength: this.pageLengthValue,

                order: this.orderValue ?? [2, "asc"],

                columns: this.columnsValue,

                language: {
                    url: this.languageUrlValue,
                },

                drawCallback: () => {

                    /*
                     * Notify siblings (e.g. bulk-actions) so they
                     * can re-sync state after a redraw.
                     */
                    this.element.dispatchEvent(
                        new CustomEvent(
                            "datatable:drawn",
                            { bubbles: true }
                        )
                    )

                    /*
                     * Legacy: sync checkboxes for the old pattern.
                     */
                    this.syncLegacyCheckboxes()
                }

            } as any
        )
    }

    /* ==============================
     * FILTERS
     * ============================== */

    initializeFilters(): void {

        // =========================
        // APPLY FILTERS
        // =========================

        const applyFilters =
            document.getElementById(
                "apply-filters"
            )

        if (applyFilters) {

            applyFilters.addEventListener(
                "click",
                () => {

                    this.datatable?.ajax.reload()
                }
            )
        }

        // =========================
        // CLEAR FILTERS
        // =========================

        const clearFilters =
            document.getElementById(
                "clear-filters"
            )

        if (clearFilters) {

            clearFilters.addEventListener(
                "click",
                () => {

                    // =====================
                    // RESET TAGS
                    // =====================

                    const tagsSelect =
                        document.querySelector(
                            '[data-dt-filter="tags"]'
                        ) as HTMLSelectElement | null;

                    if (tagsSelect) {

                        const tomSelect =
                            (tagsSelect as any)
                                .tomselect;

                        /*
                         * TomSelect
                         */
                        if (tomSelect) {

                            /*
                             * remove selected items
                             */
                            tomSelect.clear();

                            /*
                             * remove fake options
                             * added dynamically
                             */
                            tomSelect.clearOptions();

                            /*
                             * restore placeholder
                             */
                            tomSelect.refreshOptions(
                                false
                            );

                        } else {

                            /*
                             * Native select
                             */
                            tagsSelect.value = "";
                        }

                        tagsSelect.dispatchEvent(
                            new Event(
                                "change",
                                {
                                    bubbles: true
                                }
                            )
                        );
                    }

                    // =====================
                    // RESET GENERIC FILTERS
                    // =====================

                    document
                        .querySelectorAll<HTMLInputElement>(
                            "[data-dt-filter]"
                        )

                        .forEach((input) => {

                            /*
                             * radio: reset to default value ("and")
                             */
                            if (input.type === "radio") {

                                input.checked =
                                    input.value === "and"

                                return
                            }

                            if (
                                input.type === "checkbox"
                            ) {

                                /*
                                 * activos checked
                                 */
                                if (
                                    input.dataset.dtFilter
                                    === "active"
                                ) {

                                    input.checked = true

                                    return
                                }

                                /*
                                 * inactivos unchecked
                                 */
                                if (
                                    input.dataset.dtFilter
                                    === "inactive"
                                ) {

                                    input.checked = false
                                }
                            }
                        })

                    this.datatable?.ajax.reload()
                }
            )
        }

        // =========================
        // AUTO FILTERS
        // =========================

        document
            .querySelectorAll(
                "[data-dt-filter]"
            )

            .forEach((element) => {

                /*
                 * realtime checkbox
                 */
                if (
                    element instanceof HTMLInputElement
                    &&
                    element.type === "checkbox"
                ) {

                    element.addEventListener(
                        "change",
                        () => {

                            this.datatable?.ajax.reload()
                        }
                    )

                    return
                }

                /*
                 * realtime select (non-multiple)
                 */
                if (
                    element instanceof HTMLSelectElement
                    &&
                    !element.multiple
                ) {

                    element.addEventListener(
                        "change",
                        () => {

                            this.datatable?.ajax.reload()
                        }
                    )
                }
            })
    }

    /* ==============================
     * EVENT HANDLERS
     * ============================== */

    onSavedFiltersApplied = (): void => {

        this.datatable?.ajax.reload()
    }

    onReloadRequest = (): void => {

        this.reload()
    }

    beforeCache = (): void => {

        if (this.datatable) {

            this.datatable.destroy(true)

            this.datatable = null
        }
    }

    /* ==============================
     * ACTION BUTTONS (per-row)
     * ============================== */

    onClick = (e: Event): void => {

        const target =
            e.target as HTMLElement

        /*
         * Legacy select-all
         */
        if (
            target.matches(
                "[data-dt-select-all]"
            )
        ) {

            this.toggleLegacySelectAll(
                (target as HTMLInputElement).checked
            )

            return
        }

        /*
         * Legacy row checkbox
         */
        if (
            target.classList.contains(
                "dt-checkbox"
            )
        ) {

            this.toggleLegacyRow(
                target.getAttribute("value") ?? "",
                (target as HTMLInputElement).checked
            )

            return
        }

        const btn =
            target.closest<HTMLElement>(
                "[data-dt-href],[data-dt-url]"
            )

        if (!btn) {
            return
        }

        this.actionsBtns(
            btn as HTMLButtonElement
        )
    }

    async actionsBtns(
        btn: HTMLButtonElement
    ): Promise<void> {

        // =========================
        // SIMPLE NAVIGATION
        // =========================

        const href =
            btn.dataset.dtHref

        if (href) {

            const newTab = btn.dataset.dtNewTab === "true"

            if (btn.dataset.dtNavAppendFilters === "true") {

                const navUrl = new URL(href, window.location.origin)
                navUrl.searchParams.set("from_list", "1")

                document.querySelectorAll<HTMLElement>("[data-dt-filter]").forEach((filter) => {

                    const key = filter.dataset.dtFilter
                    if (!key) return

                    if (filter instanceof HTMLInputElement && filter.type === "checkbox") {
                        navUrl.searchParams.set(key, String(filter.checked))
                        return
                    }

                    if (filter instanceof HTMLSelectElement && filter.multiple) {
                        const ts = (filter as any).tomselect
                        const values: string[] = ts
                            ? ts.getValue()
                            : Array.from(filter.selectedOptions).map((o: HTMLOptionElement) => o.value)
                        values.forEach(v => navUrl.searchParams.append(`${key}[]`, v))
                        return
                    }

                    navUrl.searchParams.set(key, (filter as HTMLInputElement).value)
                })

                if (newTab) {
                    window.open(navUrl.toString(), '_blank')
                } else {
                    window.location.href = navUrl.toString()
                }

            } else {
                if (newTab) {
                    window.open(href, '_blank')
                } else {
                    window.location.href = href
                }
            }

            return
        }

        // =========================
        // REQUEST ACTIONS
        // =========================

        const url =
            btn.dataset.dtUrl

        const method =
            btn.dataset.dtMethod || "POST"

        const confirmMsg =
            btn.dataset.dtConfirm

        if (!url) {
            return
        }

        if (confirmMsg) {

            const result =
                await Swal.fire({

                    title: confirmMsg,

                    icon: "warning",

                    showCancelButton: true
                })

            if (!result.isConfirmed) {
                return
            }
        }

        await fetch(url, {
            method
        })

        this.datatable?.ajax.reload(
            undefined,
            false
        )
    }

    /* ==============================
     * LEGACY BULK (dt-checkbox / bulkRequest)
     * ==============================
     *
     * These methods support the OLD pattern used by listings that
     * still emit `.dt-checkbox` row inputs, `[data-dt-select-all]`
     * headers, and `<button data-action="click->datatable#bulkRequest">`.
     * New listings should use the `bulk-actions` Stimulus controller.
     */

    toggleLegacySelectAll(checked: boolean): void {

        if (!this.datatable) {
            return
        }

        this.datatable
            .rows({ page: "current" })
            .every((rowIdx: number) => {

                const data =
                    this.datatable!.row(rowIdx).data()

                const id = String(data.id)

                const checkbox =
                    this.element.querySelector<HTMLInputElement>(
                        `.dt-checkbox[value="${id}"]`
                    )

                if (!checkbox) {
                    return
                }

                checkbox.checked = checked

                if (checked) {
                    this.legacySelectedIds.add(id)
                } else {
                    this.legacySelectedIds.delete(id)
                }
            })

        this.updateLegacyBulkBar()
    }

    toggleLegacyRow(id: string, checked: boolean): void {

        if (checked) {
            this.legacySelectedIds.add(id)
        } else {
            this.legacySelectedIds.delete(id)
        }

        this.updateLegacySelectAllState()

        this.updateLegacyBulkBar()
    }

    syncLegacyCheckboxes(): void {

        if (!this.datatable) {
            return
        }

        this.datatable
            .rows({ page: "current" })
            .every((rowIdx: number) => {

                const data =
                    this.datatable!.row(rowIdx).data()

                const checkbox =
                    this.element.querySelector<HTMLInputElement>(
                        `.dt-checkbox[value="${data.id}"]`
                    )

                if (checkbox) {
                    checkbox.checked =
                        this.legacySelectedIds.has(String(data.id))
                }
            })

        this.updateLegacySelectAllState()

        this.updateLegacyBulkBar()
    }

    updateLegacySelectAllState(): void {

        const selectAll =
            this.element.querySelector<HTMLInputElement>(
                "[data-dt-select-all]"
            )

        if (!selectAll || !this.datatable) {
            return
        }

        const ids: string[] = []

        this.datatable
            .rows({ page: "current" })
            .every((rowIdx: number) => {
                ids.push(
                    String(
                        this.datatable!.row(rowIdx).data().id
                    )
                )
            })

        selectAll.checked =
            ids.length > 0
            && ids.every((id) => this.legacySelectedIds.has(id))
    }

    updateLegacyBulkBar(): void {

        const bulk =
            document.querySelector("[data-dt-bulk-actions]")

        if (!bulk) {
            return
        }

        bulk.classList.toggle(
            "d-none",
            this.legacySelectedIds.size === 0
        )

        const countSpan =
            bulk.querySelector("[data-dt-selected-count]")

        if (countSpan) {
            countSpan.textContent =
                String(this.legacySelectedIds.size)
        }
    }

    async bulkRequest(event: Event): Promise<void> {

        const btn = event.currentTarget as HTMLElement

        const url = btn.dataset.dtBulkUrl

        const method = btn.dataset.dtBulkMethod || "POST"

        const confirmMsg = btn.dataset.dtBulkConfirm

        const ids = Array.from(this.legacySelectedIds)

        if (!url || ids.length === 0) {
            return
        }

        if (confirmMsg) {

            const result =
                await Swal.fire({
                    title: confirmMsg,
                    text: `Se aplicará a ${ids.length} registros`,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Sí, continuar",
                    cancelButtonText: "Cancelar",
                    confirmButtonColor: "#dc3545",
                })

            if (!result.isConfirmed) {
                return
            }
        }

        Swal.showLoading()

        try {

            const response =
                await fetch(url, {
                    method,
                    headers: {
                        "Content-Type": "application/json",
                        "X-Requested-With": "XMLHttpRequest",
                    },
                    body: JSON.stringify({ ids }),
                })

            if (!response.ok) {
                throw new Error()
            }

            this.legacySelectedIds.clear()

            this.datatable?.ajax.reload(undefined, false)

            Swal.fire({
                icon: "success",
                title: "Acción completada",
                timer: 1500,
                showConfirmButton: false,
            })

        } catch (e) {

            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Ocurrió un error al ejecutar la acción.",
            })
        }
    }
}
